import { z } from "zod/v4-mini";

import { isRichTextFilled } from "@/hooks/useRichTextState";
import { TelemonitoringTier } from "@/models/CarePlanModel.ts";
import {
  ConditionType,
  conditionTypeSchema,
  MedicationSchema,
  medicationSchemeSchema,
} from "@/models/ConditionModel.ts";
import { FileModel, fileSchema } from "@/models/FileModel.ts";
import { type PumpDeviceBrand, pumpDeviceBrandSchema } from "@/models/MedicalDeviceModel.ts";
import { TeleMonitoringCriteria, telemonitoringCriteriaSchema } from "@/models/TelemonitoringCriteriaModel.ts";
import {
  termDateInFutureIfGestational,
  termDateIsValidIfGestational,
} from "@/uiKit/organisms/PatientCreationAndRenewal/termDateRules.ts";

type CarePlanDuration = "1" | "2" | "3";

export type Type1PenCarePlanRenewalInfo = {
  diabetesType: "type1";
  insulinScheme: "insulinPen";
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  periodStart: string;
  inclusionTier: TelemonitoringTier;
  pump?: undefined;
  telemonitoringNurseId?: undefined;
};

export type Type2PenCarePlanRenewalInfo = {
  diabetesType: "type2";
  insulinScheme: "insulinPen";
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  periodStart: string;
  inclusionTier: TelemonitoringTier;
  pump?: undefined;
  telemonitoringNurseId?: undefined;
};

export type Type1PumpCarePlanRenewalInfo = {
  diabetesType: "type1";
  insulinScheme: "insulinPump";
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  periodStart: string;
  inclusionTier: TelemonitoringTier;
  pump?: PumpDeviceBrand;
  telemonitoringNurseId?: undefined;
};

export type Type2PumpCarePlanRenewalInfo = {
  diabetesType: "type2";
  insulinScheme: "insulinPump";
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  periodStart: string;
  inclusionTier: TelemonitoringTier;
  pump?: PumpDeviceBrand;
  telemonitoringNurseId?: undefined;
};

export type GestationalPenCarePlanRenewalInfo = {
  diabetesType: "gestational";
  insulinScheme: "insulinPen";
  termDate: string;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  periodStart: string;
  inclusionTier: TelemonitoringTier;
  pump?: undefined;
  telemonitoringNurseId?: string;
};

export type CarePlanRenewalInfo =
  | Type1PenCarePlanRenewalInfo
  | Type2PenCarePlanRenewalInfo
  | Type1PumpCarePlanRenewalInfo
  | Type2PumpCarePlanRenewalInfo
  | GestationalPenCarePlanRenewalInfo;

export type SimpleCarePlanRenewalInfo = {
  diabetesType: ConditionType;
  insulinScheme: MedicationSchema;
  termDate?: string;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
  treatmentPrescriptionAttachment?: FileModel;
  referralLetterAttachment?: FileModel;
  pump?: PumpDeviceBrand;
  telemonitoringNurseId?: string;
};

const cannotBeGestationalAndUsePump = (data: SimpleCarePlanRenewalInfo) => {
  if (data.diabetesType === "gestational") {
    return data.insulinScheme !== "insulinPump";
  }
  return true;
};

const requiredTermDateIfGestational = (data: SimpleCarePlanRenewalInfo) => {
  if (data.diabetesType === "gestational") {
    return !!data.termDate;
  }
  return true;
};

const requiredDescription = (data: SimpleCarePlanRenewalInfo) => {
  return isRichTextFilled(data.description) || !!data.descriptionAttachment;
};

export const carePlanRenewalSchema = z
  .object(
    {
      diabetesType: conditionTypeSchema,
      insulinScheme: medicationSchemeSchema,
      // Validated by the gestational-only refinements below: a hidden leftover must not block.
      termDate: z.optional(z.string()),
      inclusionCriteria: telemonitoringCriteriaSchema,
      duration: z.literal(["1", "2", "3"]),
      description: z.optional(z.string()),
      descriptionAttachment: z.optional(fileSchema),
      treatmentPrescriptionAttachment: z.optional(fileSchema),
      referralLetterAttachment: z.optional(fileSchema),
      pump: z.optional(pumpDeviceBrandSchema),
      telemonitoringNurseId: z.optional(z.string()),
    },
    "forms.required",
  )
  .check(
    z.refine(cannotBeGestationalAndUsePump, {
      path: ["insulinSchema"],
      error: "form.required",
    }),
    z.refine(requiredTermDateIfGestational, {
      path: ["termDate"],
      error: "form.required",
    }),
    z.refine(termDateIsValidIfGestational, {
      path: ["termDate"],
      error: "form.date.invalid",
    }),
    z.refine(termDateInFutureIfGestational, {
      path: ["termDate"],
      error: "form.date.past",
    }),
    z.refine(requiredDescription, {
      path: ["description"],
      error: "diabetesForms.eitherObjectiveOrFile",
    }),
  );
